import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { getEndOfWorkDay, relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { DateTimePopoverComponent } from '@modules/form-controls/components/date-time-popover/date-time-popover.component';
import { type SendLater } from '@modules/messages/types/send-later';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { addDays } from '@sharedlib/common/utils/date';

@Component({
  standalone: true,
  selector: 'app-send-after-context-menu',
  templateUrl: './send-after-context-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ContextMenuItemComponent, ContextMenuDividerComponent, DateTimePopoverComponent],
})
export class SendAfterContextMenuComponent {
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  public readonly minDate = input(addDays(new Date(), -1));

  @Input() inputFormControl: FormControl<Date | null>;

  @Output() onSelect = new EventEmitter<Date | null>();

  protected readonly options: ReadonlyArray<{ title: string; value: SendLater }> = [
    { title: '15 minutes', value: 900 },
    { title: '1 hour', value: 3600 },
    { title: '3 hours', value: 10800 },
    // SBUX-829: offered only while the ACCOUNT's working day still has 5pm ahead of it. Judged on the
    // device's clock, a Kyiv user on a US laptop was offered a 5pm that had already passed for them.
    Date.now() < getEndOfWorkDay(new Date(), this.displayTimeZoneService.timeZone()).getTime()
      ? { title: 'End of the day (5pm)', value: 'endOfDay' as const }
      : null,
    { title: 'Tomorrow', value: 'tomorrow' as const },
  ].filter((i) => !!i);

  public dateControl = new FormControl<Date | null>(null);
  public timeControl = new FormControl<Date | null>(null);

  constructor() {
    const today = new Date();
    this.dateControl.setValue(today, { emitEvent: false });
    this.timeControl.setValue(today, { emitEvent: false });
  }

  protected handleSelect(delayPreset: (typeof this.options)[number]): void {
    this.handleSendLaterSelect(delayPreset.value);
  }

  protected handleCustomSelect(): void {
    if (!this.dateControl.value) {
      return;
    }

    const date = new Date(this.dateControl.value);

    if (this.timeControl.value) {
      date.setHours(this.timeControl.value.getHours());
      date.setMinutes(this.timeControl.value.getMinutes());
      date.setSeconds(0);
      date.setMilliseconds(0);
    }

    this.complete(date);
  }

  private handleSendLaterSelect(scheduled: SendLater): void {
    if (scheduled instanceof Date) {
      this.complete(scheduled);
      return;
    } else if (typeof scheduled === 'number') {
      // Convert seconds delay to actual date
      this.complete(new Date(Date.now() + scheduled * 1000));
      return;
    }

    let date: Date;
    switch (scheduled) {
      case 'endOfDay':
        // SBUX-829: 5pm and 9am in the ACCOUNT zone — a scheduled send is a promise about the
        // recipient-facing clock the user sees, not about the device they happened to type on.
        date = getEndOfWorkDay(new Date(), this.displayTimeZoneService.timeZone());
        break;
      case 'tomorrow':
        date = relativeDateToWorkDayStart('tomorrow', this.displayTimeZoneService.timeZone());
        break;
      default:
        throw new Error('switch statement is not exhaustive for ' + scheduled);
    }

    this.complete(date);
  }

  private complete(scheduledAt: Date | null): void {
    this.inputFormControl?.setValue?.(scheduledAt);
    // The guard that protects an unsaved compose reads `dirty`, and `setValue` does not set it.
    this.inputFormControl?.markAsDirty?.();
    this.onSelect.emit(scheduledAt);
  }
}
